import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { type AuthContextValue, AuthProvider, useAuth } from "@/auth/AuthProvider";
import { RoutePending } from "@/components/route-pending";
import { Toaster } from "@/components/ui/sonner";
import { initSentry } from "@/lib/sentry";
import { installStaleBuildReload } from "@/lib/stale-build";
// Só pelo efeito colateral: registra o listener de `beforeinstallprompt`
// no bundle de entrada, que é o mais cedo possível (ver lib/instalar.ts).
import "@/lib/instalar";
import { ThemeProvider } from "@/lib/theme";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

// PRIMEIRA coisa do arquivo a executar: erro que acontece durante o
// próprio boot do app (router, providers) é o mais difícil de reproduzir
// e o que mais precisa ser capturado. Inicializar depois deles deixaria
// justamente essa janela sem cobertura.
initSentry();

// Antes de qualquer coisa: um deploy no meio da sessão faz a próxima
// navegação pedir um chunk que não existe mais, e sem isto a tela
// simplesmente não abre.
installStaleBuildReload();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // O middleware do cliente já renova o token e reenvia a chamada, então
      // um 401 que chega aqui significa sessão perdida de verdade.
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  // Hover no desktop, touchstart no celular: o chunk da tela e as queries do
  // `loader` saem na frente do clique.
  defaultPreload: "intent",
  // O router tem um cache PRÓPRIO de preload (30s por padrão) que, se
  // ficasse ligado, faria o segundo hover na mesma tela não rodar o loader —
  // e é o loader que aquece o React Query. Zerando, o loader roda sempre e
  // quem decide o que é fresco é o `staleTime` do React Query, que é o dono
  // do cache de dados. Não há requisição a mais: o próprio React Query
  // devolve o valor em cache sem ir na rede.
  defaultPreloadStaleTime: 0,
  // O padrão é esperar 1s antes de mostrar QUALQUER coisa. Num chunk que
  // demora 400ms isso é 400ms de tela congelada sem sinal de vida. 150ms é
  // curto o bastante pra parecer resposta ao toque; `MinMs` evita que o
  // esqueleto pisque quando o chunk chega logo depois.
  defaultPendingMs: 150,
  defaultPendingMinMs: 400,
  defaultPendingComponent: RoutePending,
  scrollRestoration: true,
  context: {
    queryClient,
    // Preenchido em tempo de render por <InnerApp>, que tem acesso ao contexto.
    auth: undefined as unknown as AuthContextValue,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuth();

  // Enquanto a sessão não resolve não dá pra decidir rota: renderizar agora
  // mandaria todo mundo pro /login antes do refresh terminar.
  if (auth.status === "loading") {
    return (
      <div className="grid h-full place-items-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  // Servidor sem responder: não dá pra afirmar que a sessão acabou, então
  // ninguém é mandado pro login. A pessoa espera e tenta de novo — o cookie
  // continua lá, e quando a API voltar ela segue de onde estava.
  if (auth.status === "offline") {
    return (
      <div className="grid h-full place-items-center p-6">
        <div className="max-w-sm space-y-3 text-center">
          <p className="font-medium">Sem conexão com o servidor</p>
          <p className="text-sm text-muted-foreground">
            Não foi possível falar com o Agendo Certo. Você continua conectado — é só
            tentar de novo quando a conexão voltar.
          </p>
          <button
            type="button"
            onClick={auth.retrySession}
            className="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  return <RouterProvider router={router} context={{ auth }} />;
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Elemento #root não encontrado no index.html");

createRoot(rootElement).render(
  <StrictMode>
    {/* ThemeProvider por fora de tudo: as telas de carregando/offline do
        <InnerApp> também são pintadas com os tokens, e precisam do tema
        resolvido antes da sessão. */}
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <InnerApp />
          {/* Uma instância só, no topo: toast é camada da app, não de tela.
              A posição é do componente, que a decide por tamanho de tela. */}
          <Toaster richColors closeButton />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
